import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';

import { uiState as uiStateProxy } from '../../UIState';
import { bindValtio } from '../../ValtioController';

import '../game/components/game-notification';
import './components/changelog-link';
import './components/invite-banner';
import './components/lobby-footer';
import './components/login-status';
import './components/logon-panel';
import './components/players-online';

@customElement('airmash-lobby')
export class AirmashLobby extends LitElement {
  static override styles = css`
    :host {
      display: block;
      /* Own the coordinate space for the absolutely positioned children
         instead of relying on airmash-ui's fixed host. The host fills the
         viewport, so this is a no-op visually. */
      position: relative;
      width: 100%;
      height: 100%;
    }

    /* The top of the screen, laid out here rather than by each piece
       guessing the others' sizes. Sideways and on desktop it's one row: the
       player count centred (or the invite banner, which takes its place),
       login-status on the right, its name shrinking into the space left,
       and the What's new link on the left. The insets
       clear the notch: the status bar upright, the cutout sideways; env() is
       0 on other devices. */
    .top {
      position: absolute;
      top: calc(18px + env(safe-area-inset-top, 0px));
      left: calc(16px + env(safe-area-inset-left, 0px));
      right: calc(16px + env(safe-area-inset-right, 0px));
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
      column-gap: 20px;
      align-items: center;
    }

    .top > changelog-link {
      grid-row: 1;
      grid-column: 1;
      justify-self: start;
    }

    .top > players-online,
    .top > invite-banner {
      grid-row: 1;
      grid-column: 2;
      justify-self: center;
    }

    /* The cap and min-width: 0 let the name ellipsize to fit the column. If
       the provider and Logout alone are wider (an invite on a 667px-wide
       phone), flex-end spills them left into the gap, not off the screen. */
    .top > login-status {
      grid-row: 1;
      grid-column: 3;
      justify-self: end;
      justify-content: flex-end;
      min-width: 0;
      max-width: 100%;
    }

    /* Tighter to the edges when vertical space is short (landscape phones,
       short windows), leaving the logon panel room below. */
    @media (max-height: 500px) {
      .top {
        top: calc(4px + env(safe-area-inset-top, 0px));
        left: calc(8px + env(safe-area-inset-left, 0px));
        right: calc(8px + env(safe-area-inset-right, 0px));
      }
    }

    /* Upright the row is too narrow to share, so it stacks: the What's new
       link at the left edge, then the count or the banner, then login-status.
       The banner can be as wide as the screen, so the link keeps its own line
       instead of sitting over it. */
    @media (orientation: portrait) {
      .top {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 5px;
      }

      .top > changelog-link {
        align-self: flex-start;
      }
    }

    /* Hide the footer while a logon-panel dropdown is open. Without this,
       the menu and the footer's wrapped lines visually crowd each other on
       portrait phones. logon-panel reflects its open state via the
       'dropdown-open' attribute; this sibling selector reads it. */
    logon-panel[dropdown-open] ~ lobby-footer {
      visibility: hidden;
    }
  `;

  private uiState = bindValtio(this, uiStateProxy);

  override render() {
    // An invite banner takes the player count's place while it shows.
    return html`
      <div class="top">
        <changelog-link></changelog-link>
        ${this.uiState.invite ? html`<invite-banner></invite-banner>` : html`<players-online></players-online>`}
        <login-status></login-status>
      </div>
      <logon-panel></logon-panel>
      <lobby-footer></lobby-footer>
      <!-- Maintenance, protocol-update and startup-failure notices; the same component the game tree shows. -->
      <game-notification></game-notification>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'airmash-lobby': AirmashLobby;
  }
}
